//#region types
import { FormEnums } from "models/enums";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
} from "models/propTypes/genericFormProps";
import { TimeReportingProjectMemberFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import CategoryBillingDetailsField from "../../_partial/categoryBillingDetailsField";
import LabelField from "../../_partial/labelField";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useMemberGeneralFields(
  props: TimeReportingProjectMemberFormViewProps
): FormFieldDefinitionType<TimeReportingProjectFormTypes.ProjectMember> {
  const {
    onSearchPersonsAutoComplete,
    onProjectMemberChange,
    earliestStartDate,
    latestEndDate,
    onDatesChange,
    onCategoryActivationChange,
  } = props;
  const isValidPersonSelected = (member: TimeReportingProjectFormTypes.ProjectMember): boolean => {
    return member.id > 0;
  };
  return {
    name: {
      label: TranslationKeys.Common.Name,
      type: FormEnums.FormFieldType.AUTOCOMPLETE,
      icon: IconDefinitionConstants.WUser,
      hidden: (member: TimeReportingProjectFormTypes.ProjectMember) => !member.isNew,
      descriptionText: TranslationKeys.TimeReportingProject.AddMembersDescription,
      onChangeHandler: onProjectMemberChange,
      extraProps: {
        items: (searchStr: string) => onSearchPersonsAutoComplete(searchStr),
        referenceFieldName: FormFieldConstants.TimeReportingProject.ProjectMember.Id,
      } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    startDate: {
      label: TranslationKeys.Common.Dates,
      type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
      disabled: (member: TimeReportingProjectFormTypes.ProjectMember) => {
        return !isValidPersonSelected(member);
      },
      innerFields: {
        startDate: {
          label: TranslationKeys.TimeReportingProject.MemberStartDate,
          descriptionText: TranslationKeys.TimeReportingProject.MemberStartDateDescription,
          type: FormEnums.FormFieldType.DATE,
          disabled: (member: TimeReportingProjectFormTypes.ProjectMember) => {
            return !isValidPersonSelected(member);
          },
          widthColumnCount: 6,
          onChangeHandler: onDatesChange,
          extraProps: {
            maxDate: latestEndDate,
            minDate: earliestStartDate,
          },
        },
        endDate: {
          label: TranslationKeys.TimeReportingProject.MemberEndDate,
          descriptionText: TranslationKeys.TimeReportingProject.MemberEndDateDescription,
          type: FormEnums.FormFieldType.DATE,
          disabled: (member: TimeReportingProjectFormTypes.ProjectMember) => {
            return !isValidPersonSelected(member);
          },
          onChangeHandler: onDatesChange,
          widthColumnCount: 6,
          extraProps: {
            minDate: earliestStartDate,
            maxDate: latestEndDate,
          },
        },
      },
    },
    categories: {
      label: TranslationKeys.TimeReportingProject.SetWhichCategoriesTheProjectMemberShouldHaveAccessTo,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (member: TimeReportingProjectFormTypes.ProjectMember) => {
        return member?.categories?.length === 0;
      },
      innerFields: {
        selected: {
          label: "",
          type: FormEnums.FormFieldType.CHECKBOX,
          classes: {
            formControl: cssExports.select,
          },
          onChangeHandler: onCategoryActivationChange,
        },
        name: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <CategoryBillingDetailsField currency={props.project?.currency}></CategoryBillingDetailsField>,
          readonly: true,
        },
        accessUntilText: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <LabelField></LabelField>,
          extraProps: { hideValueIfReadOnly: true },
          readonly: (values: TimeReportingProjectFormTypes.CategoryMember) => !values.accessUntil,
          rightAlign: true,
          classes: { formControl: cssExports.validFromFieldContainer },
          widthColumnCount: 3,
        },
        accessUntil: {
          label: "",
          type: FormEnums.FormFieldType.MONTH,
          extraProps: {
            popperPositionFixed: true,
            hideDateIfReadOnly: true,
            hideClearable: true,
            setLastDayOfMonth: true,
          },
          readonly: (values: TimeReportingProjectFormTypes.CategoryMember) => !values.accessUntil,
        },
      },
      className: cssExports.categoryMemeberContainer,
      extraProps: {
        isCompactTable: true,
        selectionFieldName: "selected",
        hideActions: true,
        showColumnHeaders: true,
        onItemSelectionChange: props.onCategorySelectAllActivationChange,
        headers: [
          {
            label: "",
            fieldName: "selected",
          },
          {
            fieldName: "name",
            label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.IncludeAll),
            columnSpan: 3,
          },
        ],
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectCategory>,
    },
  };
}

export default useMemberGeneralFields;
